import React, { useState } from 'react';
import {
  Card, Form, Button, Alert, Spinner, Modal, Row, Col, InputGroup, Toast,
} from 'react-bootstrap';
import { Download, AlertCircle, Copy, CheckCircle2, ShieldCheck } from 'lucide-react';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import RequestViewer from './RequestViewer';
import { buildFielFormData } from '../../utils/fielCrypto';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const MESES = [
  { value: 0,  label: 'Todo el año' },
  { value: 1,  label: 'Enero' },
  { value: 2,  label: 'Febrero' },
  { value: 3,  label: 'Marzo' },
  { value: 4,  label: 'Abril' },
  { value: 5,  label: 'Mayo' },
  { value: 6,  label: 'Junio' },
  { value: 7,  label: 'Julio' },
  { value: 8,  label: 'Agosto' },
  { value: 9,  label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

/**
 * Tab de Declaraciones.
 *   CIEC → GET  /dec      (headers: Rfc, Secret; params: ejercicio, mes)
 *   FIEL → POST /decfiel  (FormData: llavePrivada, Certificado, Contrasena;
 *                           params: ejercicio, mes, tipoDocumento)
 * Respuesta: blob ZIP → descarga automática.
 */
const DeclaracionesTab = ({ credentials, isValid }) => {
  const { getToken } = useAuthHeaders();
  const isCiec = credentials.authMode === 'ciec';
  const vaultRegistered = credentials.signingKeyStatus?.isRegistered === true;

  const now = new Date();
  const [ejercicio, setEjercicio]       = useState(now.getFullYear());
  const [mes, setMes]                   = useState(now.getMonth() + 1);
  const [tipoDocumento, setTipoDocumento] = useState('declaracion');
  const [requestId, setRequestId]       = useState('');

  const [loading, setLoading]           = useState(false);
  const [lastRequestId, setLastRequestId] = useState('');
  const [copied, setCopied]             = useState(false);
  const [errorMsg, setErrorMsg]         = useState('');
  const [showError, setShowError]       = useState(false);
  const [showToast, setShowToast]       = useState(false);
  const [lastRequest, setLastRequest]   = useState(null);

  const [vaultSaving, setVaultSaving]   = useState(false);
  const [vaultSaveOk, setVaultSaveOk]   = useState(false);
  const [vaultSaveErr, setVaultSaveErr] = useState('');


  const downloadZip = (blob, filename) => {
    const url  = URL.createObjectURL(blob);
    const link = Object.assign(document.createElement('a'), { href: url, download: filename });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);

    try {
      const token   = await getToken();
      const headers = { 'Authorization': `Bearer ${token}`, 'Rfc': credentials.rfc };
      if (isCiec) headers['Secret'] = credentials.secret;

      const params = new URLSearchParams({ ejercicio, mes, tipoDocumento });
      if (requestId.trim()) params.set('requestId', requestId.trim());

      const endpoint = isCiec ? 'dec' : 'decfiel';
      const url      = `${API_URL_V2}/Consultar/${endpoint}?${params}`;

      let res;
      if (isCiec) {
        res = await fetch(url, { method: 'GET', headers });
      } else {
        const { formData, headers: fielHeaders } = await buildFielFormData(credentials, token);
        res = await fetch(url, { method: 'POST', headers: { ...headers, ...fielHeaders }, body: formData });
      }

      if (!res.ok) {
        const ct  = res.headers.get('content-type') || '';
        const msg = ct.includes('application/json')
          ? ((await res.json()).errorMessage || `Error ${res.status}`)
          : (await res.text() || `Error ${res.status}`);
        throw new Error(msg);
      }

      const incomingRequestId = res.headers.get('X-RequestId') || '';
      if (incomingRequestId) {
        setLastRequestId(incomingRequestId);
        setRequestId(incomingRequestId);
      }

      const blob     = await res.blob();
      const label    = isCiec ? `${ejercicio}_${mes}` : `${ejercicio}_${mes}_${tipoDocumento}`;
      const filename = `Declaraciones_${credentials.rfc}_${label}.zip`;
      downloadZip(blob, filename);
      setShowToast(true);

      setLastRequest({
        method:      isCiec ? 'GET' : 'POST',
        baseUrl:     `${API_URL_V2}/Consultar/${endpoint}`,
        queryParams: params.toString(),
        headers: {
          'Rfc': credentials.rfc,
          ...(isCiec ? { 'Secret': credentials.secret } : {}),
        },
        formFields: (isCiec || vaultRegistered) ? null : [
          { name: 'llavePrivada', type: 'file', fileName: credentials.privateKeyFile?.name || 'llave.key',        varName: 'llavePrivadaFile' },
          { name: 'Certificado',  type: 'file', fileName: credentials.certFile?.name     || 'certificado.cer',   varName: 'certificadoFile' },
          { name: 'Contrasena',   type: 'text', value: '{contrasena_fiel}' },
        ],
        jsPrefix: '// La respuesta es un ZIP binario:\n// const blob = await response.blob();\n// const url  = URL.createObjectURL(blob);\n// link.href = url; link.download = "Declaraciones.zip"; link.click();',
      });

      if (!isCiec && !vaultRegistered && credentials.saveToVault && credentials.onStoreSigningKey) {
        setVaultSaving(true);
        setVaultSaveOk(false);
        setVaultSaveErr('');
        const result = await credentials.onStoreSigningKey();
        setVaultSaving(false);
        if (result.success) setVaultSaveOk(true);
        else setVaultSaveErr(result.error || 'No se pudo guardar la FIEL en Key Vault.');
      }

    } catch (err) {
      setErrorMsg(err.message || 'Error al obtener las declaraciones.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const copyRequestId = async () => {
    try { await navigator.clipboard.writeText(lastRequestId); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentYear = now.getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

  return (
    <>
      <Card className="tab-section-card border-0 shadow-sm">
        <Card.Body>
          <Form onSubmit={handleSubmit}>

            {/* ── Request ID ──────────────────────── */}
            <Row className="mb-3">
              <Col md={8}>
                <Form.Label className="form-label-modern">
                  Request ID{' '}
                  <span className="text-muted fw-normal small">
                    (opcional — reutiliza una sesión SAT existente)
                  </span>
                </Form.Label>
                <InputGroup>
                  <Form.Control className="form-control-modern" type="text"
                    placeholder="Pega el requestId de una consulta anterior"
                    value={requestId} onChange={(e) => setRequestId(e.target.value)}
                    style={{ borderRadius: '10px 0 0 10px' }} />
                  {requestId && (
                    <Button variant="outline-secondary" onClick={() => setRequestId('')}
                      style={{ borderRadius: '0 10px 10px 0' }}>✕</Button>
                  )}
                </InputGroup>
              </Col>
            </Row>

            {/* ── Parámetros ───────────────────────── */}
            <div className="p-3 mb-3" style={{
              background: 'linear-gradient(135deg,rgba(19,160,255,.05),rgba(0,212,170,.05))',
              borderRadius: 12, border: '1px solid rgba(19,160,255,.12)',
            }}>
              <Row className="g-3">

                <Col md={3}>
                  <Form.Label className="form-label-modern">Ejercicio fiscal</Form.Label>
                  <Form.Select className="form-select-modern" value={ejercicio}
                    onChange={(e) => setEjercicio(Number(e.target.value))}>
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Label className="form-label-modern">Mes</Form.Label>
                  <Form.Select className="form-select-modern" value={mes}
                    onChange={(e) => setMes(Number(e.target.value))}>
                    {MESES.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={5}>
                  <Form.Label className="form-label-modern">Tipo de documento</Form.Label>
                  <div className="d-flex gap-3 mt-1">
                    <Form.Check type="radio" id="tipo-declaracion" label="Declaración"
                      className="custom-radio" checked={tipoDocumento === 'declaracion'}
                      onChange={() => setTipoDocumento('declaracion')} />
                    <Form.Check type="radio" id="tipo-pago" label="Pago"
                      className="custom-radio" checked={tipoDocumento === 'pago'}
                      onChange={() => setTipoDocumento('pago')} />
                    <Form.Check type="radio" id="tipo-acuse" label="Acuse"
                      className="custom-radio" checked={tipoDocumento === 'acuse'}
                      onChange={() => setTipoDocumento('acuse')} />
                  </div>
                </Col>

              </Row>
            </div>

            {!isValid && (
              <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
                <AlertCircle size={16} className="me-2 flex-shrink-0" />
                <small>Completa las credenciales de arriba para habilitar la descarga.</small>
              </Alert>
            )}

            <Button type="submit" className="btn-gradient" disabled={!isValid || loading}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Descargando declaraciones…</>
                : <><Download size={18} />Descargar Declaraciones (ZIP)</>
              }
            </Button>

          </Form>

          {/* ── Banner requestId ─────────────────── */}
          {lastRequestId && (
            <div className="mt-3 p-3 d-flex align-items-center gap-3 flex-wrap"
              style={{ background: 'rgba(0,212,170,.07)',
                border: '1px solid rgba(0,212,170,.3)', borderRadius: 10 }}>
              <CheckCircle2 size={18} color="#00d4aa" className="flex-shrink-0" />
              <div className="flex-grow-1">
                <div className="text-muted small mb-1">
                  Request ID — se reutilizará automáticamente en la próxima consulta:
                </div>
                <code style={{ fontSize: '.85rem', color: '#1a1f3a',
                  wordBreak: 'break-all', background: 'transparent' }}>
                  {lastRequestId}
                </code>
              </div>
              <Button variant="outline-success" size="sm" onClick={copyRequestId}
                className="d-flex align-items-center gap-1 flex-shrink-0">
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
          )}

          <RequestViewer request={lastRequest} />
        </Card.Body>
      </Card>

      {/* ── Toasts ─────────────────────────────── */}
      <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide
        className="custom-toast toast-success">
        <Toast.Header>
          <CheckCircle2 size={16} className="me-2 text-success" />
          <strong className="me-auto">ZIP descargado</strong>
        </Toast.Header>
        <Toast.Body>Las declaraciones se descargaron correctamente.</Toast.Body>
      </Toast>

      <Toast show={vaultSaving} className="custom-toast"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header closeButton={false}>
          <Spinner animation="border" size="sm" className="me-2" />
          <strong className="me-auto">Guardando en Key Vault…</strong>
        </Toast.Header>
      </Toast>

      <Toast show={vaultSaveOk} onClose={() => setVaultSaveOk(false)} delay={5000} autohide
        className="custom-toast toast-success"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <ShieldCheck size={14} className="me-2 text-success" />
          <strong className="me-auto">FIEL guardada en Key Vault</strong>
        </Toast.Header>
        <Toast.Body>Ya no necesitarás subir los archivos FIEL para este RFC.</Toast.Body>
      </Toast>

      <Toast show={!!vaultSaveErr} onClose={() => setVaultSaveErr('')} delay={7000} autohide
        className="custom-toast toast-error"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <AlertCircle size={14} className="me-2 text-danger" />
          <strong className="me-auto">No se pudo guardar en Key Vault</strong>
        </Toast.Header>
        <Toast.Body style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vaultSaveErr}</Toast.Body>
      </Toast>

      {/* ── Modal error ─────────────────────────── */}
      <Modal show={showError} onHide={() => setShowError(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #ff6b6b' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#1a1f3a' }}>
            <AlertCircle size={20} color="#ff6b6b" />Error al descargar declaraciones
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0" style={{ color: '#4a5568', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {errorMsg}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DeclaracionesTab;
